/** Mietvertrag zustimmen (Screen mieter/Vertrag-unterschreiben, MVP: Haken + Zeitstempel + IP + Prüfsumme; Finger-Unterschrift V2). */
import { useTx } from "../i18n";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { Button, Notice, Seitenzustand, StatusBadge } from "../ui";
import { Card, Dl } from "../ui/Layout";
import { ApiFehler } from "../api/client";
import { formatEuro } from "@shared/geld";
import { formatDatumZeit, formatTag } from "@shared/zeit";
import { mieterVertragPdfUrl, useMieterVertrag, useVertragZustimmen } from "./api";

export function MieterVertrag() {
  const tx = useTx();
  const { id = "" } = useParams();
  const q = useMieterVertrag(id);
  const zustimmen = useVertragZustimmen();
  const [gelesen, setGelesen] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  return (
    <Seitenzustand query={q} lines={6}>
      {(v) => (
        <div className="sc-col" style={{ gap: "var(--space-4)" }}>
          <div className="sc-row">
            <Link to="/mieter" className="rg-btn rg-btn--quiet" aria-label="Zurück">‹</Link>
            <h1 className="title">Mietvertrag {v.nummer}</h1>
          </div>
          {v.status === "aktiv" ? <Notice tone="success" title={tx("Zugestimmt")}>{v.zustimmungAt ? `${formatDatumZeit(v.zustimmungAt)} Uhr · ${v.zustimmungArt === "portal" ? "im Mieterportal" : "vor Ort"}` : ""}</Notice> : null}
          {v.status === "storniert" || v.status === "beendet" ? <Notice tone="info">Dieser Vertrag ist {v.status === "storniert" ? "storniert" : "beendet"}.</Notice> : null}
          {fehler ? <Notice tone="error">{fehler}</Notice> : null}
          <Card title={`Regal ${v.regal?.nummer ?? ""}`} actions={v.regal?.lage ? <StatusBadge status="shelf">{v.regal.lage}</StatusBadge> : null}>
            <Dl
              rows={[
                ["Zeitraum", v.art === "abo" ? `ab ${formatTag(v.start)}, danach je ${v.einheit === "woche" ? "Woche" : "Monat"} weiter, kündbar mit ${v.kuendigungsfristWochen} Wochen zum Monatsende` : `${formatTag(v.start)} bis ${v.ende ? formatTag(v.ende) : "–"}`],
                ["Miete", `${formatEuro(v.mieteCent)} je ${v.einheit === "woche" ? "Woche" : "Monat"}`],
                ["Provision", `${v.provisionProzent} % von jedem Verkauf`],
                ["Kaution", v.kautionCent ? `${formatEuro(v.kautionCent)} einmalig` : "keine"],
                ["Version", `${v.vertragVersion ?? "–"} · Prüfsumme ${(v.vertragTextHash ?? "").slice(0, 16).replace(/(.{4})(?=.)/g, "$1 ")}`],
              ]}
            />
            <a href={mieterVertragPdfUrl(v.id)} target="_blank" rel="noreferrer" className="rg-btn rg-btn--secondary" style={{ marginTop: "var(--space-3)" }}>Vollständigen Vertrag als PDF öffnen</a>
          </Card>
          <Card title={tx("Vertragstext")}>
            <VertragText text={v.text ?? ""} />
          </Card>
          {v.status === "reserviert" ? (
            <Card>
              <label className="sc-check"><input type="checkbox" checked={gelesen} onChange={(e) => setGelesen(e.target.checked)} /> Ich habe den Vertrag gelesen und stimme zu.</label>
              <p className="sc-muted sc-small">Mit „Zustimmen“ wird der Vertrag elektronisch geschlossen. Gespeichert werden Zeitpunkt, deine Anmeldung per E-Mail-Link, die Adresse deines Geräts und die Prüfsumme des Textes.</p>
              <Button
                variant="primary"
                size="lg"
                block
                icon="check"
                disabled={!gelesen}
                loading={zustimmen.isPending}
                onClick={() => {
                  setFehler(null);
                  zustimmen.mutate({ id: v.id, vertragTextHash: v.vertragTextHash ?? "" }, { onError: (e) => setFehler(e instanceof ApiFehler ? e.message : "Das hat nicht geklappt.") });
                }}
              >
                Zustimmen
              </Button>
            </Card>
          ) : null}
        </div>
      )}
    </Seitenzustand>
  );
}

/** Vertragstext aus der Vorlage (# / ## Überschriften, Absätze) lesbar darstellen. */
export function VertragText({ text }: { text: string }) {
  const bloecke = text.split(/\n{2,}/);
  return (
    <div className="app-vertrag">
      {bloecke.map((b, i) => {
        const zeilen = b.split("\n");
        return (
          <div key={i}>
            {zeilen.map((z, j) => (z.startsWith("# ") ? <h1 key={j}>{z.slice(2)}</h1> : z.startsWith("## ") ? <h2 key={j}>{z.slice(3)}</h2> : <p key={j} style={{ margin: "0 0 var(--space-2)" }}>{z}</p>))}
          </div>
        );
      })}
    </div>
  );
}
